import { useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { CalendarDays, Scan, Users, X } from '@/components/icons'
import { Button } from './ui/button'
import { Pill } from './ui/pill'
import { FlightFolioScanDrawer } from './FlightFolioScanDrawer'
import {
  applyFolioExtraction,
  checkFolioMatch,
  type FolioMatchResult,
} from './flight-log/applyFolioExtraction'
import type { FolioExtraction } from '@@api/flightFolioScan.shared'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
} from './ui/alert-dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { StepSection, type StepSectionBadge } from './flight-log/StepSection'
import { useFlightLogForm } from './flight-log/useFlightLogForm'
import { CrewSection } from './flight-log/sections/CrewSection'
import { RouteTimesSection } from './flight-log/sections/RouteTimesSection'
import { HoursSection } from './flight-log/sections/HoursSection'
import { FuelSection } from './flight-log/sections/FuelSection'
import { ConditionsSection } from './flight-log/sections/ConditionsSection'
import { DeiceSection } from './flight-log/sections/DeiceSection'
import { OilSection } from './flight-log/sections/OilSection'
import { EctmSection } from './flight-log/sections/EctmSection'
import { InspectionSection } from './flight-log/sections/InspectionSection'
import { DefectsSection } from './flight-log/sections/DefectsSection'
import { DocumentsSection } from './flight-log/sections/DocumentsSection'
import { SignOffSection } from './flight-log/sections/SignOffSection'
import type { FlightLogSectionKey } from '@@api/flightLog.shared'

interface FlightLogDrawerProps {
  isOpen: boolean
  onClose: () => void
  tripLegId: string
  // Force read-only (e.g. admin defect review). The drawer is also read-only
  // whenever the server reports the viewer is not a pilot crewed on this leg
  // — only the flight's own pilots may fill in, sign and submit its log.
  readOnly?: boolean
  // Open scrolled to a specific section (e.g. 'defects' for defect review);
  // otherwise every section starts collapsed
  initialSection?: FlightLogSectionKey
  // Renders a "View flight" header link that closes the drawer first
  onViewTrip?: () => void
}

export function FlightLogDrawer({
  isOpen,
  onClose,
  tripLegId,
  readOnly: forceReadOnly = false,
  initialSection,
  onViewTrip,
}: FlightLogDrawerProps) {
  // Paper-folio scanner launched from the header; the extraction is applied
  // onto this form when the pilot confirms.
  const [isScanOpen, setIsScanOpen] = useState(false)
  // Extraction held back because the sheet doesn't look like this flight —
  // applied only after the pilot confirms the mismatch dialog.
  const [mismatch, setMismatch] = useState<{
    extracted: FolioExtraction
    result: FolioMatchResult
  } | null>(null)
  const {
    context,
    readOnly,
    isLoading,
    seeded,
    saving,
    openSections,
    toggleSection,
    sectionHasError,
    approachArray,
    defectArray,
    fuelUsed,
    pic,
    statusPill,
    logNumber,
    lifecycle,
    departureDateTime,
    aircraftId,
    aircraftRegistration,
    handleClose,
    handleSaveDraft,
    handleSubmit,
    lastAutoDayRef,
    lastAutoNightRef,
    lastAutoTakeOffRef,
    lastAutoLandingRef,
    lastAutoInstrumentRef,
    solarAvailable,
    previousInspection,
    keepPreviousInspection,
    setKeepPreviousInspection,
    broughtForwardSource,
    broughtForwardMatches,
    applyBroughtForward,
  } = useFlightLogForm({
    isOpen,
    tripLegId,
    readOnly: forceReadOnly,
    initialSection,
    onClose,
  })
  const { values } = context

  const close = () => {
    setIsScanOpen(false)
    setMismatch(null)
    handleClose()
  }

  const applyExtraction = (extracted: FolioExtraction) => {
    const applied = applyFolioExtraction(extracted, values, context.setValue)
    toast.success(
      applied > 0
        ? `Applied ${applied} field${applied === 1 ? '' : 's'} from the scan`
        : 'No readable fields to apply'
    )
  }

  let sectionIndex = 0
  const sectionProps = (key: FlightLogSectionKey, title: string, badge: StepSectionBadge) => ({
    index: ++sectionIndex,
    title,
    badge,
    sectionKey: key,
    open: !!openSections[key],
    onOpenChange: toggleSection(key),
    hasError: sectionHasError(key),
  })

  return (
    <Drawer open={isOpen} onOpenChange={close}>
      <DrawerContent className="flex max-h-[92dvh] flex-col bg-surface-L0">
        <DrawerHeader className="flex h-14 shrink-0 flex-row items-center justify-between border-b border-border-L1 px-4 py-0">
          <div className="flex min-w-0 items-center gap-2">
            {/* "Flight Log #7" once the log has a number (first save); an
                unfinished log adds a Draft chip, a signed-off one shows none */}
            <DrawerTitle className="truncate font-primary text-xl font-bold tracking-[-1px] text-content-primary-bold">
              Flight Log{logNumber !== null && ` #${logNumber}`}
            </DrawerTitle>
            {logNumber !== null && lifecycle !== 'complete' && (
              <Pill variant="draft" className="shrink-0">
                Draft
              </Pill>
            )}
          </div>
          <div className="flex items-center gap-3">
            {onViewTrip && (
              <button
                type="button"
                className="text-xs font-semibold text-content-brand-bold active:opacity-70"
                onClick={() => {
                  close()
                  onViewTrip()
                }}
              >
                View flight
              </button>
            )}
            {!readOnly && (
              <Button
                variant="brand"
                size="sm"
                roundness="round"
                onClick={() => setIsScanOpen(true)}
              >
                <Scan />
                Scan
              </Button>
            )}
            <DrawerClose asChild>
              <Button variant="secondary" size="icon" roundness="round" className="size-8">
                <X className="size-[15px]" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex flex-col gap-3 px-4 pb-4 pt-4">
          {!seeded ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-surface-L2" />
            ))
          ) : (
            <>
              {/* Trip summary card */}
              <div className="rounded-2xl border border-border-L1 bg-surface-L1 px-4 py-2">
                <div className="flex items-center justify-between gap-2 py-2">
                  <p className="min-w-0 truncate text-base font-semibold tracking-[-0.5px] text-content-primary-bold">
                    {values.routeTimes.from} → {values.routeTimes.to}
                    {aircraftRegistration && (
                      <span className="font-medium text-content-primary-subtle">
                        {'  •  '}
                        {aircraftRegistration}
                      </span>
                    )}
                  </p>
                  <Pill variant={statusPill.variant}>{statusPill.label}</Pill>
                </div>
                <div className="flex items-center justify-between border-t border-border-L1 pb-2 pt-3">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="size-5 text-content-primary-bold" />
                    <span className="text-sm text-content-primary-bold">
                      {departureDateTime ? dayjs(departureDateTime).format('D MMM YYYY') : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="size-5 text-content-primary-bold" />
                    <span className="text-sm text-content-primary-bold">
                      PAX {values.routeTimes.paxCount}
                    </span>
                  </div>
                </div>
              </div>

              <StepSection {...sectionProps('crew', 'Crew', 'required')}>
                <CrewSection context={context} />
              </StepSection>

              <StepSection {...sectionProps('routeTimes', 'Route & Times', 'required')}>
                <RouteTimesSection context={context} />
              </StepSection>

              <StepSection {...sectionProps('hours', 'Airframe & Engines', 'required')}>
                <HoursSection
                  context={context}
                  part="airframeEngines"
                  broughtForwardSource={broughtForwardSource}
                  broughtForwardMatches={broughtForwardMatches}
                  onApplyBroughtForward={applyBroughtForward}
                />
              </StepSection>

              <StepSection {...sectionProps('apu', 'APU', 'required')}>
                <HoursSection
                  context={context}
                  part="apu"
                  broughtForwardSource={broughtForwardSource}
                  broughtForwardMatches={broughtForwardMatches}
                  onApplyBroughtForward={applyBroughtForward}
                />
              </StepSection>

              <StepSection {...sectionProps('fuel', 'Fuel', 'required')}>
                <FuelSection context={context} fuelUsed={fuelUsed} />
              </StepSection>

              <StepSection {...sectionProps('conditions', 'Flight Conditions', 'required')}>
                <ConditionsSection
                  context={context}
                  approachArray={approachArray}
                  dayTimeAuto={
                    lastAutoDayRef.current !== null &&
                    values.conditions.dayTime === lastAutoDayRef.current
                  }
                  nightTimeAuto={
                    lastAutoNightRef.current !== null &&
                    values.conditions.nightTime === lastAutoNightRef.current
                  }
                  takeOffAuto={
                    lastAutoTakeOffRef.current !== null &&
                    values.conditions.takeOff === lastAutoTakeOffRef.current
                  }
                  landingAuto={
                    lastAutoLandingRef.current !== null &&
                    values.conditions.landing === lastAutoLandingRef.current
                  }
                  instrumentTimeAuto={
                    lastAutoInstrumentRef.current !== null &&
                    values.conditions.instrumentTime === lastAutoInstrumentRef.current
                  }
                  solarAvailable={solarAvailable}
                />
              </StepSection>

              <StepSection {...sectionProps('deice', 'De-ice / Anti-ice', 'conditional')}>
                <DeiceSection context={context} />
              </StepSection>

              <StepSection {...sectionProps('oil', 'Oil', 'optional')}>
                <OilSection context={context} />
              </StepSection>

              <StepSection {...sectionProps('ectm', 'Engine Condition Monitoring', 'optional')}>
                <EctmSection context={context} />
              </StepSection>

              <StepSection {...sectionProps('inspection', 'Next Inspection', 'required')}>
                <InspectionSection
                  context={context}
                  hasPreviousInspection={previousInspection !== null}
                  keepPrevious={keepPreviousInspection}
                  onKeepPreviousChange={setKeepPreviousInspection}
                />
              </StepSection>

              <StepSection {...sectionProps('defects', 'Defects', 'conditional')}>
                <DefectsSection context={context} defectArray={defectArray} />
              </StepSection>

              <StepSection {...sectionProps('documents', 'Documents', 'optional')}>
                <DocumentsSection context={context} />
              </StepSection>

              <StepSection {...sectionProps('signOff', 'Captain Sign off', 'required')}>
                <SignOffSection context={context} hasPic={!!pic} />
              </StepSection>
            </>
          )}
        </div>

        {!readOnly && (
          <DrawerFooter className="flex-row gap-2 border-t border-border-L1 p-4">
            <Button
              variant="secondary"
              size="mini"
              className="flex-1"
              onClick={handleSaveDraft}
              disabled={!!saving || isLoading}
            >
              {saving === 'draft' ? 'Saving…' : 'Save draft'}
            </Button>
            <Button
              variant="primary"
              size="mini"
              className="flex-1"
              onClick={handleSubmit}
              disabled={!!saving || isLoading}
            >
              {saving === 'submit' ? 'Submitting…' : 'Submit'}
            </Button>
          </DrawerFooter>
        )}
      </DrawerContent>

      {/* Header scan action: photograph the paper folio, then apply the
          extracted fields onto this form (pilot reviews before saving). */}
      {!readOnly && (
        <FlightFolioScanDrawer
          open={isScanOpen}
          onOpenChange={setIsScanOpen}
          aircraftId={aircraftId}
          onApply={(extracted) => {
            // Does the photographed sheet belong to this flight? On a
            // registration or route mismatch, hold the extraction and ask.
            const result = checkFolioMatch(extracted, {
              from: values.routeTimes.from,
              to: values.routeTimes.to,
              registration: aircraftRegistration,
            })
            if (result.registrationMismatch || !result.routeMatch) {
              setMismatch({ extracted, result })
              return
            }
            applyExtraction(extracted)
          }}
        />
      )}

      {/* Scan ↔ flight mismatch confirmation */}
      <AlertDialog
        open={mismatch !== null}
        onOpenChange={(open) => {
          if (!open) setMismatch(null)
        }}
      >
        <AlertDialogContent className="gap-0 p-8 text-center">
          <AlertDialogTitle className="text-center text-xl font-semibold leading-[1.2] tracking-[-0.4px]">
            Folio doesn't match this flight
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="mt-4 flex flex-col gap-1 text-center text-sm text-content-primary-subtle">
              {mismatch?.result.registrationMismatch && (
                <p>
                  The sheet reads registration{' '}
                  <span className="font-semibold text-content-primary-bold">
                    {mismatch.result.registrationMismatch.scanned}
                  </span>{' '}
                  — this flight is{' '}
                  <span className="font-semibold text-content-primary-bold">
                    {mismatch.result.registrationMismatch.expected}
                  </span>
                  .
                </p>
              )}
              {mismatch && !mismatch.result.routeMatch && (
                <p>
                  No leg row for{' '}
                  <span className="font-semibold text-content-primary-bold">
                    {values.routeTimes.from} → {values.routeTimes.to}
                  </span>{' '}
                  was found on the sheet.
                </p>
              )}
              <p className="mt-1">Apply the scanned values anyway?</p>
            </div>
          </AlertDialogDescription>
          <div className="mt-4 flex w-full flex-col gap-2">
            <Button
              variant="primary"
              size="mini"
              className="w-full"
              onClick={() => {
                if (mismatch) applyExtraction(mismatch.extracted)
                setMismatch(null)
              }}
            >
              Apply anyway
            </Button>
            <Button
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={() => setMismatch(null)}
            >
              Cancel
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Drawer>
  )
}
